import {useState} from "react";
import {Link, useNavigate, useParams} from "react-router-dom";

import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {
  ArrowLeftIcon,
  Loader2Icon,
  PowerIcon,
  RotateCwIcon,
  Trash2Icon,
} from "lucide-react";
import {toast} from "sonner";

import {
  serversDestroy,
  serversGet,
  serversReboot,
  serversStart,
  serversStop,
} from "@/sdk";

import {Badge} from "@/components/ui/badge";
import {Button} from "@/components/ui/button";
import {Card, CardContent, CardHeader, CardTitle} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import {
  formatGB,
  formatMB,
  formatTimestamp,
  serverStateVariant,
} from "@/lib/format";

export const ServerDetail = () => {
  const {id} = useParams<{id: string}>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [destroyOpen, setDestroyOpen] = useState(false);

  const queryKey = ["servers", "get", id] as const;

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const {data, error} = await serversGet({path: {id: id!}});
      if (error) throw error;
      return data!;
    },
    enabled: !!id,
    refetchInterval: 5000,
  });

  const invalidate = () => qc.invalidateQueries({queryKey});

  const startM = useMutation({
    mutationFn: () => serversStart({path: {id: id!}}).then((r) => r.data),
    onSuccess: () => {
      toast.success("Server starting");
      invalidate();
    },
    onError: () => toast.error("Start failed"),
  });
  const stopM = useMutation({
    mutationFn: () =>
      serversStop({path: {id: id!}, body: {force: false}}).then((r) => r.data),
    onSuccess: () => {
      toast.success("Server stopping");
      invalidate();
    },
    onError: () => toast.error("Stop failed"),
  });
  const rebootM = useMutation({
    mutationFn: () =>
      serversReboot({path: {id: id!}, body: {force: false}}).then((r) => r.data),
    onSuccess: () => {
      toast.success("Server rebooting");
      invalidate();
    },
    onError: () => toast.error("Reboot failed"),
  });
  const destroyM = useMutation({
    mutationFn: () => serversDestroy({path: {id: id!}}).then((r) => r.data),
    onSuccess: () => {
      toast.success("Server destroyed");
      navigate("/servers", {replace: true});
    },
    onError: () => toast.error("Destroy failed"),
  });

  if (query.isLoading) {
    return (
      <div className="flex items-center justify-center p-12 text-muted-foreground">
        <Loader2Icon className="size-5 animate-spin" />
      </div>
    );
  }

  if (query.isError || !query.data) {
    return (
      <Card>
        <CardContent className="p-6 text-sm">
          <p className="text-destructive">Could not load server.</p>
          <Button variant="outline" size="sm" className="mt-4" asChild>
            <Link to="/servers">
              <ArrowLeftIcon /> Back
            </Link>
          </Button>
        </CardContent>
      </Card>
    );
  }

  const s = query.data;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon-sm" asChild>
          <Link to="/servers" aria-label="Back to servers">
            <ArrowLeftIcon />
          </Link>
        </Button>
        <div className="flex-1">
          <h1 className="font-heading text-2xl font-semibold">{s.hostname}</h1>
          {s.label ? (
            <p className="text-sm text-muted-foreground">{s.label}</p>
          ) : null}
        </div>
        <Badge variant={serverStateVariant(s.state)} className="capitalize">
          {s.state || "unknown"}
        </Badge>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button
          onClick={() => startM.mutate()}
          disabled={startM.isPending || s.state === "running"}
        >
          <PowerIcon /> Start
        </Button>
        <Button
          variant="outline"
          onClick={() => stopM.mutate()}
          disabled={stopM.isPending || s.state === "stopped"}
        >
          <PowerIcon /> Stop
        </Button>
        <Button variant="outline" onClick={() => rebootM.mutate()} disabled={rebootM.isPending}>
          <RotateCwIcon /> Reboot
        </Button>
        <Button variant="destructive" onClick={() => setDestroyOpen(true)}>
          <Trash2Icon /> Destroy
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Resources</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <Row label="vCPUs" value={String(s.vcpus)} />
            <Row label="Memory" value={formatMB(Math.round(s.memory_bytes / (1024 * 1024)))} />
            <Row
              label="Disk"
              value={formatGB(Math.round(s.disk_bytes / (1024 * 1024 * 1024)))}
            />
            <Row label="Plan ID" value={s.plan_id} mono />
            <Row label="OS image ID" value={s.os_image_id ?? "—"} mono />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Network</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <Row label="Primary IPv4" value={s.primary_ipv4 ?? "—"} mono />
            <Row label="Primary IPv6" value={s.primary_ipv6 ?? "—"} mono />
            <Row label="MAC" value={s.primary_mac ?? "—"} mono />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Lifecycle</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <Row label="State" value={s.state} />
            <Row label="Desired state" value={s.desired_state} />
            <Row label="Created" value={formatTimestamp(s.created_at)} />
            <Row label="Provisioned" value={formatTimestamp(s.provisioned_at)} />
            <Row label="Last seen" value={formatTimestamp(s.last_seen_at)} />
            <Row label="Suspended" value={formatTimestamp(s.suspended_at)} />
            <Row label="Terminated" value={formatTimestamp(s.terminated_at)} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Identifiers</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <Row label="Eyrie ID" value={s.id} mono />
            <Row label="Libvirt UUID" value={s.libvirt_uuid ?? "—"} mono />
            <Row label="Lair ID" value={s.lair_id ?? "—"} mono />
            <Row label="Organization ID" value={s.organization_id} mono />
            <Row label="Owner user ID" value={s.owner_user_id} mono />
            <Row label="Blesta service ID" value={s.blesta_service_id ?? "—"} mono />
          </CardContent>
        </Card>
      </div>

      <Dialog open={destroyOpen} onOpenChange={setDestroyOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Destroy server?</DialogTitle>
            <DialogDescription>
              This will terminate <span className="font-mono">{s.hostname}</span> and remove all
              associated storage. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDestroyOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={destroyM.isPending}
              onClick={() => destroyM.mutate()}
            >
              {destroyM.isPending ? "Destroying…" : "Destroy"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

function Row({label, value, mono}: {label: string; value: string; mono?: boolean}) {
  return (
    <div className="flex items-center justify-between gap-3 border-b py-1.5 last:border-0">
      <span className="text-muted-foreground">{label}</span>
      <span className={mono ? "truncate font-mono text-xs" : "truncate"} title={value}>
        {value}
      </span>
    </div>
  );
}
